<template>
    <div
        class="ls-single-signup-view"
        :class="{
            'ls-single-signup-view--show-bg-image': showBg,
        }"
    >
        <template v-if="!showBg">
            <ls-header-brand v-if="isStandAloneTreatment" />
            <ls-header v-else />
        </template>
        <ls-modals-container></ls-modals-container>
        <transition name="ls-fade" mode="out-in" appear>
            <router-view></router-view>
        </transition>
    </div>
</template>

<script>
import _ from 'lodash';
import { mapState, mapActions } from 'vuex';
import lsHeader from '@/components/original-mydot/header/Header.vue';
import lsHeaderBrand from '@/components/original-mydot/header/HeaderBrand.vue';
import * as types from '@/store/types';
import lsGoogleService from '@/services/google/google.service';
import lsQuerystringService from '@/services/querystring/querystring.service';
import { lsErrorService, lsStorageService } from 'ls-vue-common';
import {
    generalStorageConstants,
    savedOrderModuleConstants,
    savedAddressModuleConstants,
} from '@/services/storage/storage-key.constants';
import lsModalsContainer from '@/components/original-mydot/modals/ModalsContainer.vue';
import { serviceType } from '@/constants';
// eslint-disable-next-line import/no-cycle
import { resolveIntent } from '@/router/router.helpers';

export default {
    name: 'SingleSignupParent',
    components: {
        lsHeader,
        lsHeaderBrand,
        lsModalsContainer,
    },
    computed: {
        ...mapState(['address', 'order', 'screenSize', 'loading', 'featureFlags']),
        showBg() {
            // show bg and hide header on get pricing when not getting quote
            return this.$route.name === 'getPricing' && !this.loading.quotingOrder;
        },
        isStandAloneTreatment() {
            return serviceType.TREATMENT === this.order.intent;
        },
    },
    created() {
        resolveIntent(this.$route, this.setIntent);
        if (parseInt(this.$route.params.ls_lb, 10) === 1) {
            this.setLoadingType({ loadingType: 'progressBar' });
        }
        this.parseRouteParams();
    },
    methods: {
        ...mapActions({
            setLoadingType: types.LOADING__SET_LOADING_TYPE,
            setIntent: types.ORDER__UPDATE_INTENT,
            orderUpdateName: types.ORDER__UPDATE_NAME,
            orderUpdatePhone: types.ORDER__UPDATE_PHONE,
            orderUpdateEmail: types.ORDER__UPDATE_EMAIL,
            orderUpdatePropertyId: types.ORDER__UPDATE_PROPERTY_ID,
            orderUpdateOrder: types.ORDER__UPDATE_ORDER,
            orderCreateQuote: types.ORDER__CREATE_QUOTE,
            googlePredictionSelected: types.ADDRESS__GOOGLE_PREDICTION_SELECTED,
            quotingOrder: types.LOADING__QUOTING_ORDER,
            orderErrored: types.LOADING__ORDER_ERRORED,
            resumeAddress: types.ADDRESS__RESUME_ADDRESS,
            updateAddress: types.ADDRESS__UPDATE_ADDRESS,
            getPricingUpdateState: types.GET_PRICING__UPDATE_STATE,
        }),
        parseRouteParams() {
            const validCookies = lsQuerystringService.checkCookies();
            const validFields = lsQuerystringService.checkParams(this.$route.params);
            if (this.$route.params.email) {
                this.getPricingUpdateState({ showEmail: true });
                if (!this.$route.params.phone) {
                    this.getPricingUpdateState({ showPhone: false });
                }
            }
            // handle name and/or address if it arrives from qs
            if (this.$route.params.name) {
                this.orderUpdateName({ name: this.$route.params.name });
            }

            if (this.$route.params.phone) {
                this.orderUpdatePhone({ phone: this.$route.params.phone });
            }

            if (this.$route.params.email) {
                this.orderUpdateEmail({ phone: this.$route.params.email });
            }

            if (this.$route.params.propertyId) {
                this.orderUpdatePropertyId({ propertyId: this.$route.params.propertyId });
            }

            // handle address and googlePlace flag if it arrives from qs
            // can hit this with either googlePlaceId or address
            if (this.$route.params.googlePlaceId || this.$route.params.address) {
                // add valid cookies / fields to order prior to creation attempts
                this.orderUpdateOrder({
                    ...validCookies,
                    ...validFields,
                });

                if (this.$route.params.googlePlaceId || this.$route.params.googlePlace) {
                    this.quotingOrder();

                    // if placeid exists use this over address, otherwise get details from first prediction
                    const getPlaceRequest = this.$route.params.googlePlaceId
                        ? lsGoogleService.getDetails({ placeId: this.$route.params.googlePlaceId })
                        : lsGoogleService.getDetailsFromFirstPrediction(this.$route.params.address);

                    getPlaceRequest
                        .then((place) => {
                            this.googlePredictionSelected(place);
                            this.orderCreateQuote({
                                address: this.address,
                                type: this.order.intent,
                            });
                        })
                        .catch((error) => {
                            this.orderErrored();
                            this.updateAddress({ formatted_address: this.$route.params.address });
                            lsErrorService.log({ error });
                        });
                } else {
                    this.updateAddress({ formatted_address: this.$route.params.address });
                }
            } else if (lsStorageService.get(generalStorageConstants.ORDER_CREATED)) {
                // Attempt to resume order with data from local storage
                const savedAddressModule = lsStorageService.get(savedAddressModuleConstants.MODULE);
                if (savedAddressModule) {
                    this.resumeAddress(savedAddressModule);
                }
                this.orderUpdateOrder({
                    address: lsStorageService.get(savedOrderModuleConstants.ADDRESS),
                    name: lsStorageService.get(savedOrderModuleConstants.NAME),
                    fname: lsStorageService.get(savedOrderModuleConstants.FNAME),
                    lname: lsStorageService.get(savedOrderModuleConstants.LNAME),
                    phone: lsStorageService.get(savedOrderModuleConstants.PHONE),
                    email: lsStorageService.get(savedOrderModuleConstants.EMAIL),
                    deal: lsStorageService.get(savedOrderModuleConstants.DEAL),
                    property_id: lsStorageService.get(savedOrderModuleConstants.PROPERTY_ID),
                    saved_order:
                        (lsStorageService.get(savedOrderModuleConstants.SAVED_ORDER) || 0) + 1,
                    lead_source: lsStorageService.get(savedOrderModuleConstants.LEAD_SOURCE),
                    ...validCookies,
                    ...validFields,
                });

                this.orderCreateQuote({ address: this.address, type: this.order.intent }).catch(
                    (error) => {
                        this.orderErrored();
                        lsErrorService.log({ error });
                    }
                );
            } else if (!_.isEmpty(validCookies) && !_.isEmpty(validFields)) {
                this.orderUpdateOrder({
                    ...validCookies,
                    ...validFields,
                });
            }
        },
    },
};
</script>

<style lang="scss">
@import '@/scss/original-mydot/main.scss';

.ls-single-signup-view {
    min-height: 100vh;
}

.ls-single-signup-view--show-bg-image {
    background-image: url('../../../assets/modal-bg-lawn.jpg');
    background-repeat: no-repeat;
    background-position: 50%;
    background-attachment: fixed;
    background-size: cover;
}
</style>
